import React, { useEffect } from 'react'
import Routes from 'Routes'
import useWebSocket from 'utils/useWebSocket'
import AccessibilityWidget from 'components/AccessibilityWidget'
import AuditSecurityGuard from 'components/AuditSecurityGuard'
import 'assets/css/index.css'
import './assets/css/App.css'
import './assets/css/Main.css'
import './assets/css/Custom.css'

function App() {
  useWebSocket(process.env.REACT_APP_SERVER_WSS_URL)

  useEffect(() => {
    const fixComboboxAria = () => {
      const comboboxes = document.querySelectorAll('input[role="combobox"], .ant-select-selection-search-input')
      comboboxes.forEach((input) => {
        if (!input.hasAttribute('role')) {
          input.setAttribute('role', 'combobox')
        }
        const isExpanded = input.closest('.ant-select-open') !== null
        const expandedValue = isExpanded ? 'true' : 'false'
        if (input.getAttribute('aria-expanded') !== expandedValue) {
          input.setAttribute('aria-expanded', expandedValue)
        }
        if (!input.hasAttribute('aria-haspopup')) {
          input.setAttribute('aria-haspopup', 'listbox')
        }

        // Copy aria-owns to aria-controls if aria-controls is missing (required by ARIA 1.2 for combobox)
        const ariaOwns = input.getAttribute('aria-owns')
        if (ariaOwns && !input.hasAttribute('aria-controls')) {
          input.setAttribute('aria-controls', ariaOwns)
        }

        // Fix invalid aria-activedescendant when popup list is closed/unmounted
        const activeDescendant = input.getAttribute('aria-activedescendant')
        if (activeDescendant && !document.getElementById(activeDescendant)) {
          input.removeAttribute('aria-activedescendant')
        }

        // Pass aria-label from parent .ant-select container down to inner input
        if (!input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) {
          const parentSelect = input.closest('.ant-select')
          const parentLabel = parentSelect ? parentSelect.getAttribute('aria-label') : null
          if (parentLabel) {
            input.setAttribute('aria-label', parentLabel)
          } else {
            input.setAttribute('aria-label', 'Select option')
          }
        }
      })

      // Fix scrollable regions keyboard access across all tables, descriptions, modals, lists, main content, and overflow containers
      const scrollables = document.querySelectorAll(
        'main, .ant-layout-content, .content, ' +
        '.ant-table, .ant-table-container, .ant-table-content, .ant-table-body, .ant-table-header, .ant-table-bordered, ' +
        '.rc-virtual-list-holder, .rc-virtual-list, .rc-virtual-list-holder-inner, .ant-select-dropdown, ' +
        '.ant-descriptions-view, .ant-descriptions, .ant-modal-body, .ant-drawer-body, ' +
        '[style*="overflow"], [style*="scroll"], [class*="scroll"], [class*="table-body"], [class*="descriptions-view"]'
      )
      scrollables.forEach((el) => {
        if (el.id?.includes('digio') || (typeof el.className === 'string' && el.className.includes('digio'))) {
          return
        }
        const style = window.getComputedStyle(el)
        const hasOverflow = style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflowX === 'auto' || style.overflowX === 'scroll' || el.style.overflow?.includes('scroll') || el.style.overflow?.includes('auto')
        const isTargetClass = el.classList.contains('ant-descriptions-view') || el.classList.contains('ant-table-body') || el.classList.contains('ant-table-content') || el.classList.contains('ant-layout-content') || el.tagName === 'MAIN'
        if (hasOverflow || isTargetClass) {
          if (!el.hasAttribute('tabindex')) {
            el.setAttribute('tabindex', '0')
            if (!el.hasAttribute('aria-label')) {
              el.setAttribute('aria-label', el.tagName === 'MAIN' || el.classList.contains('ant-layout-content') ? 'Main content region' : 'Scrollable details content')
            }
          }
        }
      })

      // Clean up any attributes on third-party Digio SDK containers
      const digioContainers = document.querySelectorAll('[id*="digio"], [class*="digio"]')
      digioContainers.forEach((el) => {
        if (el.tagName === 'DIV') {
          el.removeAttribute('tabindex')
          el.removeAttribute('aria-label')
          el.removeAttribute('aria-labelledby')
        }
      })

      // Ensure all Ant Design select options have role="option" and their parent container has role="listbox" with aria-label
      const selectOptions = document.querySelectorAll('.ant-select-item-option')
      selectOptions.forEach((option) => {
        if (!option.hasAttribute('role')) {
          option.setAttribute('role', 'option')
        }
        const parent = option.parentElement
        if (parent) {
          if (!parent.hasAttribute('role')) {
            parent.setAttribute('role', 'listbox')
          }
          if (!parent.hasAttribute('aria-label') && !parent.hasAttribute('aria-labelledby')) {
            parent.setAttribute('aria-label', 'Select options list')
          }
        }
      })

      // Enforce high-contrast solid text color on all Ant Design Select selected items & disabled selects
      const selectItems = document.querySelectorAll('.ant-select-selection-item, .ant-select-selection-placeholder, .ant-select-disabled, .ant-select-disabled .ant-select-selector')
      selectItems.forEach((item) => {
        item.style.setProperty('color', '#262626', 'important')
        item.style.setProperty('opacity', '1', 'important')
      })

      // Fix empty listbox missing role="option" children & ensure aria-label present
      const listboxes = document.querySelectorAll('[role="listbox"], .ant-select-dropdown [id$="_list"]')
      listboxes.forEach((listbox) => {
        if (!listbox.hasAttribute('role')) {
          listbox.setAttribute('role', 'listbox')
        }
        if (!listbox.hasAttribute('aria-label') && !listbox.hasAttribute('aria-labelledby')) {
          listbox.setAttribute('aria-label', 'Select options list')
        }
        const options = listbox.querySelectorAll('[role="option"], [role="group"]')
        if (options.length === 0) {
          let emptyItem = listbox.querySelector('.ant-select-item-empty')
          if (emptyItem) {
            emptyItem.setAttribute('role', 'option')
            emptyItem.setAttribute('aria-selected', 'false')
          }
        }
      })

      // Fix invalid aria-controls when target element ID does not exist in DOM (excluding combobox inputs)
      const ariaControlsElements = document.querySelectorAll('[aria-controls]:not([role="combobox"]):not(.ant-select-selection-search-input)')
      ariaControlsElements.forEach((el) => {
        const targetId = el.getAttribute('aria-controls')
        if (targetId && !document.getElementById(targetId)) {
          el.removeAttribute('aria-controls')
        }
      })

      // Ensure tablist parent exists on .ant-tabs-nav and clean up intermediate wrappers & icons
      const tablists = document.querySelectorAll('[role="tablist"], .ant-tabs-nav')
      tablists.forEach((tablist) => {
        if (!tablist.hasAttribute('role')) {
          tablist.setAttribute('role', 'tablist')
        }
        const nonTabChildren = tablist.querySelectorAll('div:not([role="tab"]):not([role="presentation"]):not([role="none"]), span:not([role="tab"]):not([role="presentation"]):not([role="none"])')
        nonTabChildren.forEach((child) => {
          child.setAttribute('role', 'presentation')
        })
      })

      // Fix prohibited ARIA attributes on presentation/none elements (e.g. aria-label on presentation spans)
      const presentationElements = document.querySelectorAll('[role="presentation"][aria-label], [role="presentation"][aria-labelledby], [role="none"][aria-label], [role="none"][aria-labelledby]')
      presentationElements.forEach((el) => {
        el.removeAttribute('aria-label')
        el.removeAttribute('aria-labelledby')
      })

      // Clean up invalid ARIA attribute values (e.g. capitalized "True"/"False" strings) across the DOM
      const ariaValueFixes = document.querySelectorAll('[aria-expanded], [aria-hidden], [aria-selected], [aria-checked], [aria-disabled], [aria-invalid]')
      ariaValueFixes.forEach((el) => {
        ['aria-expanded', 'aria-hidden', 'aria-selected', 'aria-checked', 'aria-disabled', 'aria-invalid'].forEach((attr) => {
          if (el.hasAttribute(attr)) {
            const val = el.getAttribute(attr)
            if (val === 'True' || val === 'TRUE') el.setAttribute(attr, 'true')
            if (val === 'False' || val === 'FALSE') el.setAttribute(attr, 'false')
          }
        })
      })

      // Ensure all ARIA dialogs and modal wrappers have an accessible name
      const dialogNodes = document.querySelectorAll('.ant-modal-wrap, [role="dialog"], [role="alertdialog"]')
      dialogNodes.forEach((dlg) => {
        if (!dlg.hasAttribute('aria-label') && !dlg.hasAttribute('aria-labelledby')) {
          const modalTitle = dlg.querySelector('.ant-modal-title, [id^="rcDialogTitle"]')
          if (modalTitle && modalTitle.id) {
            dlg.setAttribute('aria-labelledby', modalTitle.id)
          } else if (modalTitle && modalTitle.innerText && modalTitle.innerText.trim()) {
            dlg.setAttribute('aria-label', modalTitle.innerText.trim())
          } else {
            dlg.setAttribute('aria-label', 'Modal Dialog Window')
          }
        }
      })

      // Ensure empty table headers have discernible text. An aria-label alone
      // satisfies screen readers but some automated checks specifically require
      // real (if visually hidden) text content, so add both: a visually-hidden
      // span with the label text plus the aria-label as a redundant fallback.
      const emptyTableHeaders = document.querySelectorAll('th.ant-table-cell-scrollbar, th.ant-table-row-expand-icon-cell, th.ant-table-cell:empty')
      emptyTableHeaders.forEach((th) => {
        if (!th.innerText || !th.innerText.trim()) {
          const isExpand = th.classList.contains('ant-table-row-expand-icon-cell')
          const labelText = isExpand ? 'Expand details' : 'Table layout column'
          if (!th.hasAttribute('aria-label')) {
            th.setAttribute('aria-label', labelText)
          }
          if (!th.querySelector('.a11y-visually-hidden-label')) {
            const hiddenSpan = document.createElement('span')
            hiddenSpan.className = 'a11y-visually-hidden-label'
            hiddenSpan.textContent = labelText
            hiddenSpan.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;'
            th.appendChild(hiddenSpan)
          }
        }
      })

      // Ensure layout landmarks have semantic ARIA roles
      const headers = document.querySelectorAll('header, .ant-layout-header')
      headers.forEach((h) => {
        if (!h.hasAttribute('role')) h.setAttribute('role', 'banner')
      })
      const siders = document.querySelectorAll('aside, .ant-layout-sider')
      siders.forEach((s) => {
        if (!s.hasAttribute('role')) s.setAttribute('role', 'complementary')
        if (!s.hasAttribute('aria-label')) s.setAttribute('aria-label', 'Sidebar Navigation')
      })
      // Only the site-wide footer should be a contentinfo landmark; a page-specific
      // section styled with the same Ant Design footer class (e.g. a "contact us"
      // block nested inside the main content) must not also become one, or the
      // page ends up with more than one contentinfo landmark.
      const footers = document.querySelectorAll('footer, .ant-layout-footer')
      footers.forEach((f) => {
        const isNestedInMain = f.closest('main, [role="main"], .ant-layout-content')
        if (isNestedInMain) return
        if (!f.hasAttribute('role')) f.setAttribute('role', 'contentinfo')
      })
      // Some third-party scripts (e.g. translation widgets) stamp role="region" on
      // the main content area on initial load; that satisfies "a" landmark but not
      // the "exactly one main landmark" requirement, so promote it to role="main"
      // whenever it isn't already main (verified this correction isn't fought back
      // by anything re-asserting "region" afterward).
      const mains = document.querySelectorAll('main, .ant-layout-content')
      mains.forEach((m) => {
        if (m.getAttribute('role') !== 'main') m.setAttribute('role', 'main')
      })

      // Clean up invalid or empty heading roles across all tags, buttons, tabs, and legend containers
      const invalidHeadings = document.querySelectorAll(
        '.ant-tag[role="heading"], .ant-badge[role="heading"], .ant-btn[role="heading"], a[role="heading"], .ant-table-cell[role="heading"], ' +
        '.ant-tabs-content[role="heading"], .ant-tabs-tabpane[role="heading"], [role="heading"]'
      )
      invalidHeadings.forEach((el) => {
        const isTabOrLegend = el.classList.contains('ant-tabs-content') || el.classList.contains('ant-tabs-tabpane')
        const isInvalidTag = el.closest('.ant-tag, .ant-badge, .ant-btn, button, a, .ant-table-cell')
        const isEmptyText = !el.innerText || !el.innerText.trim()
        if (isTabOrLegend || isInvalidTag || isEmptyText) {
          el.removeAttribute('role')
          el.removeAttribute('aria-level')
        }
      })

      // Fix heading role for Ant Design PageHeader, main_dashboard_div, and explicit titles with non-empty text
      const headingCandidates = document.querySelectorAll(
        '.ant-page-header-heading-title, .main_dashboard_div > div > div, .ant-modal-title, [id^="rcDialogTitle"], ' +
        '[class*="header"], [class*="Header"], [class*="title"], [class*="Title"], [class*="heading"], [class*="Heading"], label'
      )
      headingCandidates.forEach((el) => {
        if (el.closest('.ant-tag, .ant-badge, .ant-btn, button, a, .ant-tabs-content, .ant-tabs-tabpane, table, th, td')) {
          return
        }
        if (!el.innerText || !el.innerText.trim()) {
          return
        }
        // Avatars, badges, and similar identity/status chips are never real
        // headings even when their initials/text render at a large font size;
        // auto-tagging them as headings creates false heading-level jumps.
        const elClassName = typeof el.className === 'string' ? el.className.toLowerCase() : ''
        if (elClassName.includes('avatar') || elClassName.includes('badge')) {
          return
        }
        if (!el.hasAttribute('role')) {
          const style = window.getComputedStyle(el)
          const fontSize = parseInt(style.fontSize) || 14
          const className = typeof el.className === 'string' ? el.className.toLowerCase() : ''
          const isModal = el.classList.contains('ant-modal-title') || el.id?.startsWith('rcDialogTitle')
          const isAntInternal = typeof el.className === 'string' && /(?:^|\s)ant-/.test(el.className)
          const isPageTitle = el.classList.contains('ant-page-header-heading-title') || (!isAntInternal && el.parentElement?.parentElement?.classList.contains('main_dashboard_div')) || (el.tagName === 'LABEL' && fontSize >= 18)
          const isExplicitHeading = el.classList.contains('ant-page-header-heading-title') || isModal || className.includes('title') || className.includes('heading')

          if (isExplicitHeading || isPageTitle || fontSize >= 18) {
            el.setAttribute('role', 'heading')
            el.setAttribute('aria-level', isPageTitle ? '1' : '2')
          }
        }

        if (el.classList.contains('ant-modal-title') || el.id?.startsWith('rcDialogTitle')) {
          const innerSpans = el.querySelectorAll('span, div')
          innerSpans.forEach((child) => {
            if (!child.closest('.ant-tag, .ant-btn') && !child.hasAttribute('role') && child.innerText && child.innerText.trim()) {
              child.setAttribute('role', 'heading')
              child.setAttribute('aria-level', '2')
            }
          })
        }
      })

      // Fix heading role on table header report title cells (.report-title, .main-title)
      const tableReportTitles = document.querySelectorAll('.report-title, .main-title, .main_div_title, [class*="report-title"]')
      tableReportTitles.forEach((th) => {
        if (!th.closest('.ant-tag, .ant-btn') && (!th.hasAttribute('role') || th.getAttribute('role') === 'columnheader') && th.innerText && th.innerText.trim()) {
          th.setAttribute('role', 'heading')
          th.setAttribute('aria-level', '2')
        }
      })

      // Fix heading role on Ant Design Collapse panel headers (.ant-collapse-header)
      const collapseHeaders = document.querySelectorAll('.ant-collapse-header')
      collapseHeaders.forEach((header) => {
        if (header.getAttribute('role') !== 'heading') header.setAttribute('role', 'heading')
        if (header.getAttribute('aria-level') !== '2') header.setAttribute('aria-level', '2')
        const textNodes = header.querySelectorAll('span, div')
        textNodes.forEach((child) => {
          if (!child.classList.contains('ant-collapse-arrow') && !child.classList.contains('anticon') && child.innerText && child.innerText.trim()) {
            if (child.getAttribute('role') !== 'heading') child.setAttribute('role', 'heading')
            if (child.getAttribute('aria-level') !== '2') child.setAttribute('aria-level', '2')
          }
        })
      })

      // Fix nested interactive controls inside Ant Design Upload components
      const uploadWrappers = document.querySelectorAll('.ant-upload[role="button"]')
      uploadWrappers.forEach((upload) => {
        const innerInteractive = upload.querySelector('button, input, a, [tabindex="0"]')
        if (innerInteractive) {
          upload.removeAttribute('role')
          upload.removeAttribute('tabindex')
        }
      })

      // Fix missing title/accessible name on iframe elements
      const iframes = document.querySelectorAll('iframe')
      iframes.forEach((iframe) => {
        if (!iframe.hasAttribute('title') && !iframe.hasAttribute('aria-label') && !iframe.hasAttribute('aria-labelledby')) {
          iframe.setAttribute('title', 'Application Content Frame')
        }
      })

      // Fix missing accessible label on Ant Design Checkbox inputs
      const checkboxes = document.querySelectorAll('.ant-checkbox-input')
      checkboxes.forEach((input) => {
        if (!input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby') && !input.hasAttribute('id')) {
          const wrapper = input.closest('.ant-checkbox-wrapper, label')
          if (wrapper) {
            const labelText = wrapper.innerText || wrapper.textContent
            if (labelText && labelText.trim()) {
              input.setAttribute('aria-label', labelText.trim())
            } else {
              input.setAttribute('aria-label', 'Checkbox option')
            }
          }
        }
      })

      // Fix missing accessible labels on unlabelled form inputs (Inputs, TextAreas, Radio inputs, Selects, InputNumbers)
      const formInputs = document.querySelectorAll('input:not([type="hidden"]), textarea, select')
      formInputs.forEach((input) => {
        const inputId = input.getAttribute('id')
        let hasMatchingLabel = false
        if (inputId) {
          try {
            hasMatchingLabel = document.querySelector(`label[for="${CSS.escape(inputId)}"]`) !== null
          } catch (e) {
            hasMatchingLabel = document.querySelector(`label[for="${inputId}"]`) !== null
          }
        }
        const hasAriaLabel = input.hasAttribute('aria-label') || input.hasAttribute('aria-labelledby')

        if (!hasMatchingLabel && !hasAriaLabel) {
          const tr = input.closest('tr')
          const scriptCell = tr ? tr.querySelector('td:nth-child(2), td:nth-child(1), [class*="script"], [class*="name"]') : null
          const scriptText = scriptCell ? scriptCell.innerText?.trim() : null

          const formItem = input.closest('.ant-form-item, .form-group, fieldset, td, div')
          const formLabel = formItem ? formItem.querySelector('.ant-form-item-label label, label, [class*="label"]') : null
          const labelText = formLabel ? formLabel.innerText?.trim().replace('*', '').trim() : null

          if (scriptText) {
            input.setAttribute('aria-label', `${scriptText} Quantity`)
          } else if (labelText) {
            input.setAttribute('aria-label', labelText)
          } else if (input.placeholder && input.placeholder.trim()) {
            input.setAttribute('aria-label', input.placeholder.trim())
          } else if (inputId) {
            const formattedId = inputId.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()
            input.setAttribute('aria-label', formattedId)
          } else {
            input.setAttribute('aria-label', input.name || input.type || 'Form input field')
          }
        }
      })

      // Fix missing accessible names on icon-only buttons & DatePicker navigation buttons
      const superPrevBtns = document.querySelectorAll('.ant-picker-header-super-prev-btn')
      superPrevBtns.forEach(btn => { if (!btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', 'Previous year') })

      const prevBtns = document.querySelectorAll('.ant-picker-header-prev-btn')
      prevBtns.forEach(btn => { if (!btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', 'Previous month') })

      const nextBtns = document.querySelectorAll('.ant-picker-header-next-btn')
      nextBtns.forEach(btn => { if (!btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', 'Next month') })

      const superNextBtns = document.querySelectorAll('.ant-picker-header-super-next-btn')
      superNextBtns.forEach(btn => { if (!btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', 'Next year') })

      const buttons = document.querySelectorAll('button, .ant-btn')
      buttons.forEach((btn) => {
        if (!btn.hasAttribute('aria-label') && !btn.hasAttribute('aria-labelledby') && (!btn.innerText || !btn.innerText.trim())) {
          if (btn.classList.contains('ant-picker-header-super-prev-btn')) {
            btn.setAttribute('aria-label', 'Previous year')
          } else if (btn.classList.contains('ant-picker-header-prev-btn')) {
            btn.setAttribute('aria-label', 'Previous month')
          } else if (btn.classList.contains('ant-picker-header-next-btn')) {
            btn.setAttribute('aria-label', 'Next month')
          } else if (btn.classList.contains('ant-picker-header-super-next-btn')) {
            btn.setAttribute('aria-label', 'Next year')
          } else {
            const icon = btn.querySelector('.anticon, [class*="icon"]')
            if (icon) {
              const iconClass = Array.from(icon.classList).find(c => c.includes('icon'))
              const label = iconClass ? iconClass.replace('ant-picker-', '').replace('-icon', '').replace('anticon-', '').replace('-', ' ') : 'Action button'
              btn.setAttribute('aria-label', label || 'Action button')
            } else if (btn.title) {
              btn.setAttribute('aria-label', btn.title)
            } else {
              btn.setAttribute('aria-label', 'Button')
            }
          }
        }
      })

      // Fix missing alt/accessible names on images inside breadcrumbs, links, and standalone icons
      const images = document.querySelectorAll('img')
      images.forEach((img) => {
        const src = img.getAttribute('src') || ''
        const isBreadcrumb = img.closest('.ant-breadcrumb, [class*="breadcrumb"]') !== null
        const isHomeIcon = src.includes('HomeIcon') || src.includes('home') || isBreadcrumb
        if (isHomeIcon) {
          if (img.getAttribute('alt') !== 'Home') img.setAttribute('alt', 'Home')
          if (img.getAttribute('aria-label') !== 'Home') img.setAttribute('aria-label', 'Home')
        } else if (!img.hasAttribute('alt') || img.getAttribute('alt') === '') {
          const parentLink = img.closest('a, button, [role="button"]')
          if (parentLink) {
            const linkText = parentLink.innerText?.trim() || parentLink.getAttribute('aria-label')
            img.setAttribute('alt', linkText || 'Navigation icon')
          } else {
            img.setAttribute('alt', 'Decorative image')
          }
        }
      })

      // Ensure third-party widgets and injected spans (e.g. Chatling, Sienna, Recharts) attached to body are contained within landmarks or hidden
      Array.from(document.body.children).forEach(child => {
        if (child.id === 'root' || ['SCRIPT', 'STYLE', 'NOSCRIPT', 'LINK', 'META'].includes(child.tagName)) return
        
        // If it's visually hidden or a known measurement span, ensure it has aria-hidden
        if (child.id === 'recharts_measurement_span' || child.style.display === 'none' || child.style.visibility === 'hidden') {
          if (!child.hasAttribute('aria-hidden')) child.setAttribute('aria-hidden', 'true')
        } else if (!child.hasAttribute('role') && !child.hasAttribute('aria-hidden') && child.className !== 'gtranslate_wrapper') {
          child.setAttribute('role', 'region')
          child.setAttribute('aria-label', child.id || child.className || child.tagName || 'Third party widget')
        }
      })

      // Ensure all iframes have titles (common issue with third-party widgets)
      document.querySelectorAll('iframe:not([title])').forEach(iframe => {
        iframe.setAttribute('title', iframe.id || iframe.name || iframe.className || 'Embedded Content')
      })
    }
    fixComboboxAria()

    // Re-apply on any DOM change (new content) as well as on attribute resets:
    // some third-party components (e.g. Ant Design's Layout.Sider) re-render and
    // drop attributes we previously patched in, so watching attributes too keeps
    // the fixes from silently disappearing after navigation or re-renders.
    let scheduled = false
    const scheduleFix = () => {
      if (scheduled) return
      scheduled = true
      requestAnimationFrame(() => {
        scheduled = false
        fixComboboxAria()
      })
    }
    const observer = new MutationObserver(scheduleFix)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'role', 'aria-label', 'aria-labelledby', 'aria-level', 'aria-expanded',
        'aria-haspopup', 'aria-controls', 'aria-hidden', 'aria-selected',
        'aria-checked', 'aria-disabled', 'aria-invalid', 'tabindex', 'alt', 'title',
      ],
    })
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <AuditSecurityGuard />
      <Routes />
      <AccessibilityWidget />
    </>
  )
}

export default App

